import { getWebViewPlatform } from "@b2bportal/core-utilities";
import {
  type RBCUniversalEventParameters,
  createRBCEvent,
} from "./rbcAnalytics";
import {
  ENABLE_NATIVE_BRIDGE,
  RBC_ANALYTICS_VERSION,
} from "@/config/nativeBridge";

/**
 * Posts an event to RBC's native analytics framework via the webview bridge
 * @param eventName - The event name
 * @param language - The current language
 * @param properties - Optional event properties
 */
export const postToNativeBridge = (
  eventName: string,
  language: string,
  properties?: Record<string, any>
): void => {
  try {
    if (!ENABLE_NATIVE_BRIDGE) {
      return;
    }

    const platform = getWebViewPlatform();
    if (!platform) {
      return;
    }

    const universalEventParameters: RBCUniversalEventParameters = {
      loyalty_surrogate_id: properties?.loyalty_surrogate_id,
      confirmation_id: properties?.confirmation_id,
      value: properties?.value,
      search_term: properties?.search_term,
    };

    const analyticsEvent = createRBCEvent(
      eventName,
      RBC_ANALYTICS_VERSION,
      language,
      universalEventParameters,
      properties
    );

    // Only post events that match RBC patterns
    if (!analyticsEvent) {
      return;
    }

    const payload = JSON.stringify(analyticsEvent);

    if (platform === "ios") {
      const webkit = (window as any).webkit;
      if (!webkit?.messageHandlers?.analyticsFrameworkEvent) {
        console.warn("RBC native bridge unavailable on iOS webview");
        return;
      }
      webkit.messageHandlers.analyticsFrameworkEvent.postMessage(payload);
    } else if (platform === "android") {
      const webViewFragment = (window as any).WebViewFragment;
      if (!webViewFragment?.onAnalyticsFrameworkEvent) {
        console.warn("RBC native bridge unavailable on Android webview");
        return;
      }
      webViewFragment.onAnalyticsFrameworkEvent(payload);
    }
  } catch (error) {
    console.error("Failed to post event to RBC native bridge:", error);
  }
};

/**
 * Posts a feature event to RBC's native app (non-analytics)
 * Used for triggering native app actions like updating points display
 * @param name - The feature name (e.g., "travel")
 * @param value - The action value (e.g., "update-points")
 *
 * Note: iOS uses featureEvent, Android uses onFeatureEventWebHook
 */
export const postFeatureEventToNativeBridge = (
  name: string,
  value: string
): void => {
  try {
    if (!ENABLE_NATIVE_BRIDGE) {
      return;
    }

    const platform = getWebViewPlatform();
    if (!platform) {
      return;
    }

    const payload = {
      version: RBC_ANALYTICS_VERSION,
      type: "value",
      name,
      value,
    };

    const jsonPayload = JSON.stringify(payload);

    if (platform === "ios") {
      // iOS uses webkit message handler
      const webkit = (window as any).webkit;
      if (!webkit?.messageHandlers?.featureEvent) {
        console.warn("RBC feature event bridge unavailable on iOS webview");
        return;
      }
      webkit.messageHandlers.featureEvent.postMessage(jsonPayload);
    } else if (platform === "android") {
      // Android uses WebHook method
      const webViewFragment = (window as any).WebViewFragment;
      if (!webViewFragment?.onFeatureEventWebHook) {
        console.warn("RBC feature event bridge unavailable on Android webview");
        return;
      }
      webViewFragment.onFeatureEventWebHook(jsonPayload);
    }
  } catch (error) {
    console.error("Failed to post feature event to RBC native bridge:", error);
  }
};

/**
 * Check if the native bridge is available
 * Useful for debugging or conditional logic
 */
export const isNativeBridgeAvailable = (): boolean => {
  try {
    const platform = getWebViewPlatform();

    if (platform === "ios") {
      return !!(window as any).webkit?.messageHandlers?.analyticsFrameworkEvent;
    } else if (platform === "android") {
      return !!(window as any).WebViewFragment?.onAnalyticsFrameworkEvent;
    }

    return false;
  } catch (e) {
    return false;
  }
};

/** Commerce-compatible deferred action; Avion host registration must be verified before rollout. */
export const closeNativeChat = (): boolean => {
  const bridge = window as Window & {
    webkit?: {
      messageHandlers?: {
        deferredActions?: { postMessage(message: string): void };
      };
    };
    Android?: { deferredActions(message: string): void };
  };
  const message = JSON.stringify({ key: "closeChat" });
  try {
    const ios = bridge.webkit?.messageHandlers?.deferredActions;
    if (typeof ios?.postMessage === "function") {
      ios.postMessage(message);
      return true;
    }
    if (typeof bridge.Android?.deferredActions === "function") {
      bridge.Android.deferredActions(message);
      return true;
    }
  } catch {
    console.error("[HtsAssist] Native chat dismissal failed");
  }
  return false;
};
